import { useEffect, useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import {
  EOrderStatus,
  EOrderType,
  IOrder,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../types/order'
import { useFormik } from 'formik'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/Clear'
import { PDFThaiPost } from './PDFThaiPost'
import { FlashLabelSize, PDFFlash } from './PDFFlash'
import { pdf } from '@react-pdf/renderer'
import CircularLoading from '../../../components/CircularProgress'
import { PdfUtil } from '../../../utils/pdf.util'
import { Tag } from '../../../components/Tag'
import dayjs from 'dayjs'
import { ONLY_DATE_TIME_FORMAT, VAT_TYPES } from '../../../constants/global'
import PDFReceipt from '../../Order/components/PDFReceipt'
import { useUser } from '../../../hooks/useUser'
import { RiPrinterLine } from 'react-icons/ri'
import { numberFormat } from '../../../utils'
import PageUrls from '../../../constants/page-urls'

interface IPrintTrackFormModalProps {
  trackingProvider?: OrderShippingProvider
  isOpen: boolean
  onClose: (visible: boolean) => void
  orders: IOrder[]
  onUpdateTrackingSuccess: () => void
}

interface IPrintTrackFormModal {
  size: string | number
  orders: IOrder[]
}

interface ITracking extends IOrder {
  error?: string
}

enum ESizesJnt {
  S100X75 = '2',
  S100X80 = '3',
  S100X100 = '4',
  S150X100 = '1',
  S76X100 = '5',
}

enum ESizesFlash {
  S100X75 = 'small',
  S100X180 = 'large',
}

enum EFlashLabelType {
  SYSTEM = 'system',
  FLASH = 'flash',
}

enum ESizesThaiPost {
  S100X75 = 2.95276,
  S100X150 = 5.90551,
  S100X180 = 7.08661,
}

enum ESizesKerry {
  S100x75 = '100x75',
  S100x150 = '100x150',
  S100x180 = '100x180',
}

const menuSizes: any = {
  [`${OrderShippingProvider.FLASH}`]: [
    { value: ESizesFlash.S100X75, label: '100x75' },
    { value: ESizesFlash.S100X180, label: '100x180' },
  ],
  [`${OrderShippingProvider.JNT}`]: [
    { value: ESizesJnt.S100X75, label: '100x75' },
    { value: ESizesJnt.S100X80, label: '100x80' },
    { value: ESizesJnt.S100X100, label: '100x100' },
    { value: ESizesJnt.S150X100, label: '150x100' },
  ],
  [`${OrderShippingProvider.THAIPOST}`]: [
    { value: ESizesThaiPost.S100X75, label: '100x75' },
    { value: ESizesThaiPost.S100X150, label: '100x150' },
    { value: ESizesThaiPost.S100X180, label: '100x180' },
  ],
  [`${OrderShippingProvider.KERRY}`]: [
    { value: ESizesKerry.S100x75, label: '100x75' },
    { value: ESizesKerry.S100x150, label: '100x150' },
    { value: ESizesKerry.S100x180, label: '100x180' },
  ],
  // [`${OrderShippingProvider.BESTEXPRESS}`]: [
  //   { value: ESizesKerry.S100x75, label: '100x75' },
  //   { value: ESizesKerry.S100x150, label: '100x150' },
  //   { value: ESizesKerry.S100x180, label: '100x180' },
  // ],
  // [`${OrderShippingProvider.SPXEXPRESS}`]: [
  //   { value: '80mm', label: '80mm' },
  //   { value: 'A4', label: 'A4' },
  // ],
  // [`${OrderShippingProvider.BESTEXPRESS}`]: [
  //   { value: 'A4', label: 'A4' },
  // ],
}

export const PrintTrackFormModal = (props: IPrintTrackFormModalProps) => {
  const { merchant } = useUser()
  const { trackingProvider, isOpen, onClose, orders, onUpdateTrackingSuccess } =
    props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPrintSuccess, setIsPrintSuccess] = useState<boolean>(true)
  const [showItemInPack, setShowItemInPack] = useState<boolean>(true)
  const [showItemAsTable, setShowItemAsTable] = useState<boolean>(false)
  const [isPrintReceipt, setIsPrintReceipt] = useState<boolean>(true)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [flashLabelType, setFlashLabelType] = useState<EFlashLabelType>(
    EFlashLabelType.FLASH,
  )
  const [showFullPhone, setShowFullPhone] = useState<boolean>(false)
  const isFlashSelfLabel =
    trackingProvider === OrderShippingProvider.FLASH &&
    flashLabelType === EFlashLabelType.SYSTEM

  const [printReceipt, setPrintReceipt] = useState<{
    vat: VAT_TYPES
    size: 'A4' | '80mm'
    showDate: string
  }>({
    vat: VAT_TYPES.EXCLUDE,
    size: 'A4',
    showDate: 'order',
  })

  useEffect(() => {
    setIsLoading(true)
    formik.setFieldValue('orders', orders)
    setIsLoading(false)
  }, [isOpen, orders])

  useEffect(() => {
    if (isOpen) {
      setFlashLabelType(EFlashLabelType.FLASH)
    }
  }, [isOpen])

  const formik = useFormik<IPrintTrackFormModal>({
    initialValues: {
      size: '',
      orders: [],
    } as IPrintTrackFormModal,
    onSubmit: async (values: IPrintTrackFormModal) => {
      const provider = trackingProvider
      if (!provider) {
        message.error('กรุณาเลือกผู้ให้บริการขนส่ง')
        return
      }

      if (!values.size && menuSizes?.[provider]) {
        message.error('กรุณาเลือกขนาด')
        return
      }

      setIsLoading(true)


      const pdfUtil = new PdfUtil(provider)
      pdfUtil.setPopupMessageHandler(setPopupMessage)

      const ordersSelected = values.orders.filter((order: ITracking) => order.status === EOrderStatus.TRACKING)
      const orderIds = ordersSelected.map((order: ITracking) => order.objectId)
      if (orderIds.length === 0) {
        message.error('กรุณาเลือกเฉพาะคำสั่งซื้อ ที่มีสถานะ สร้าง Tracking เท่านั้น')
        setIsLoading(false)
        return
      }

      // const mismatchedOrders = ordersSelected.filter(
      //   (order: ITracking) => order.shippingProvider !== trackingProvider
      // )
      // if (mismatchedOrders.length > 0) {
      //   message.error(
      //     `พบคำสั่งซื้อ ${mismatchedOrders.length} รายการ ที่ไม่ตรงกับผู้ให้บริการขนส่ง ${trackingProvider}`
      //   )
      //   setIsLoading(false)
      //   return
      // }

      switch (trackingProvider) {
        case OrderShippingProvider.JNT:
        case OrderShippingProvider.SPXEXPRESS:
          await pdfUtil.openLink({
            provider: trackingProvider,
            orders: orderIds,
            note: '',
            size: values.size,
          })
          pdfUtil.setSuccessOrderIds(orderIds)
          break
        case OrderShippingProvider.THAIPOST:
          const thaiPostHeight = values.size as number
          const blob = await pdf(
            <PDFThaiPost
              showItemInPack={showItemInPack}
              showItemAsTable={showItemAsTable}
              orders={values.orders}
              width={3.93701}
              height={thaiPostHeight}
            />,
          ).toBlob()
          pdfUtil.setSuccessOrderIds(orderIds)
          const pdfURL = URL.createObjectURL(blob)
          window.open(pdfURL, '_blank')
          break
        case OrderShippingProvider.FLASH:
          if (!isFlashSelfLabel) {
            await pdfUtil.flash(orderIds, values.size)
            break
          }
          try {
            const { fallback, failed } = await pdfUtil.flashSelfLabel(
              orderIds,
              values.size,
              (ids, labels) => {
                const idSet = new Set(ids)
                return pdf(
                  <PDFFlash
                    orders={ordersSelected.filter((order: ITracking) =>
                      idSet.has(order.objectId),
                    )}
                    labels={labels}
                    size={values.size as FlashLabelSize}
                    showFullPhone={showFullPhone}
                    showItemInPack={showItemInPack}
                    showItemAsTable={showItemAsTable}
                  />,
                ).toBlob()
              },
            )
            if (fallback > 0) {
              message.info(
                `ใช้ใบปะหน้าของ Flash แทน ${fallback} รายการ (ไม่พบข้อมูลรหัสคัดแยก)`,
              )
            }
            if (failed > 0) {
              message.warning(`พิมพ์ไม่ได้ ${failed} รายการ`)
            }
          } catch (e) {
            console.error(e)
            pdfUtil.setSuccessOrderIds([])
            message.error(
              'ดึงข้อมูลใบปะหน้าไม่สำเร็จ กรุณาลองใหม่ หรือเลือกใบปะหน้า Flash Express',
            )
          }
          break
        case OrderShippingProvider.BESTEXPRESS:
          await pdfUtil.bestexpress(orderIds)
          break
        case OrderShippingProvider.KERRY:
          const orders = values.orders.map((order: ITracking) => {
            let note = order.items
              .map((item) => {
                return `${item.productName} ${item.quantity ? '(' + item.quantity + ')' : ''
                  } ${item?.meta?.size ? `ขนาด ${item.meta.size}` : ''}`
              })
              .join(', ')

            if (order.paymentMethod === EOrderType.COD) {
              note += ` COD ${numberFormat(order.total)}`
            }
            return {
              trackingCode: order.trackingCode,
              orderId: order.objectId,
              senderAddress: {
                name: order?.sender?.name || '',
                address: order?.sender?.address || '',
                subdistrict: order?.sender?.subDistrict || '',
                district: order?.sender?.district || '',
                province: order?.sender?.province || '',
                zipcode: order?.sender?.zipcode || '',
              },
              recipientAddress: {
                name: order.recipient.name,
                address: order.recipient.address,
                subdistrict: order.recipient.subDistrict,
                district: order.recipient.district,
                province: order.recipient.province,
                zipcode: order.recipient.zipcode,
              },
              size: values.size,
              note,
            }
          })
          await pdfUtil.kerry(orders)
          break
      }

      const orderSuccessIds = pdfUtil.getSuccessOrderIds()
      const orders = values.orders.filter((order: ITracking) =>
        orderSuccessIds.includes(order.objectId),
      )
      const ordersIdWithTrackings = orders.map((order: IOrder) => {
        return {
          order: order.objectId,
          tracking: order.trackingCode,
        }
      })
      const promise = [
        pdfUtil.setOrderIsPrinted(
          orderSuccessIds,
          trackingProvider,
          ordersIdWithTrackings,
        ),
      ]
      if (isPrintReceipt) {
        if (orders.length > 0) {
          promise.push(openPDF(orders))
        } else {
          message.error('ไม่พบรายการคำสั่งซื้อ ที่พิมพ์สำเร็จ')
        }
      }

      await Promise.all(promise)

      setIsPrintSuccess(false)
      onClose(false)
      onUpdateTrackingSuccess()
      setIsLoading(false)
    },
  })

  const openPDF = async (selectedOrders: IOrder[]) => {
    const blob = await pdf(
      <PDFReceipt
        merchant={merchant}
        orders={selectedOrders}
        size={printReceipt.size}
        vatType={printReceipt.vat}
        showDate={printReceipt.showDate}
      />,
    ).toBlob()
    const pdfURL = URL.createObjectURL(blob)
    window.open(pdfURL, '_blank')
  }

  const columns: TableColumnsType<ITracking & { key: number }> = [
    {
      title: 'ชื่อ',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 150,
      render: (recipient: OrderRecipient) => (
        <Grid container spacing={1} flexDirection={'column'}>
          <Grid item>
            <Typography variant="h6" color="text.secondary">
              {recipient.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.address} {recipient.subDistrict} {recipient.district}{' '}
              {recipient.province} {recipient.zipcode}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.phone}
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'รหัสคำสั่งซื้อ',
      dataIndex: 'orderCode',
      key: 'orderCode',
      align: 'center' as const,
      width: 80,
      render: (orderCode: number, order: IOrder) => (
        <>
          <a
            href={`${PageUrls.Orders.Detail}/${order.objectId}`}
            target="_blank"
          >
            <Tag color={'success'}>{order.code}</Tag>
          </a>
        </>
      ),
    },
    {
      title: 'PRINT',
      dataIndex: 'printedAt',
      key: 'printedAt',
      align: 'center' as const,
      width: 100,
      render: (printedAt: number) => (
        <>
          <Typography variant="subtitle2" color="text.error">
            <Tag color={printedAt ? 'success' : 'error'}>
              {printedAt ? 'พิมพ์' : 'ยังไม่พิมพ์'}
            </Tag>
          </Typography>
          {printedAt && (
            <Typography
              sx={{ mt: 1 }}
              variant="subtitle2"
              color="text.secondary"
            >
              {dayjs(printedAt).format(ONLY_DATE_TIME_FORMAT)}
            </Typography>
          )}
        </>
      ),
    },
    {
      title: 'รหัส Tracking',
      dataIndex: 'trackingCode',
      key: 'trackingCode',
      width: 220,
      render: (trackingCode: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {trackingCode}
        </Typography>
      ),
    },
  ]

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
    >
      <CircularLoading message={popupMessage} isLoading={isLoading} />
      <DialogTitle>พิมพ์ใบปะหน้า จำนวน
        {formik.values.orders.filter((order: ITracking) => order.status === EOrderStatus.TRACKING).length} รายการ
        ({trackingProvider?.toUpperCase()})
      </DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            {menuSizes?.[trackingProvider ?? ''] && (
              <>
                {trackingProvider === OrderShippingProvider.FLASH && (
                  <Grid item xs={12} lg={12}>
                    <RadioGroup
                      row
                      value={flashLabelType}
                      onChange={(e) =>
                        setFlashLabelType(e.target.value as EFlashLabelType)
                      }
                    >
                      <FormControlLabel
                        value={EFlashLabelType.SYSTEM}
                        control={<Radio />}
                        label="ใบปะหน้าในระบบ"
                      />
                      <FormControlLabel
                        value={EFlashLabelType.FLASH}
                        control={<Radio />}
                        label="Flash Express"
                      />
                    </RadioGroup>
                  </Grid>
                )}
                <Grid item xs={12} lg={12}>
                  <FormControl variant="filled" fullWidth>
                    <InputLabel id="size-label">เลือกขนาด</InputLabel>
                    <Select
                      id="size"
                      name="size"
                      labelId="size-label"
                      value={formik.values.size}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                    >
                      <MenuItem disabled value="" selected>
                        <em>เลือกขนาด</em>
                      </MenuItem>
                      {(
                        trackingProvider === OrderShippingProvider.THAIPOST &&
                          showItemAsTable
                          ? menuSizes?.[trackingProvider ?? '']?.filter(
                            (item: any) =>
                              item.value === ESizesThaiPost.S100X150 ||
                              item.value === ESizesThaiPost.S100X180,
                          )
                          : isFlashSelfLabel && showItemAsTable
                            ? menuSizes?.[trackingProvider ?? '']?.filter(
                              (item: any) =>
                                item.value === ESizesFlash.S100X180,
                            )
                            : menuSizes?.[trackingProvider ?? '']
                      )?.map((item: any, index: number) => (
                        <MenuItem value={item.value} key={index}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {trackingProvider === OrderShippingProvider.THAIPOST && (
                  <>
                    <Grid item xs={12} lg={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showItemInPack}
                            onChange={(e) => setShowItemInPack(e.target.checked)}
                          />
                        }
                        label="แสดงสินค้าย่อยของเซตในหมายเหตุใบ LABEL"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showItemAsTable}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setShowItemAsTable(checked)
                              if (checked) {
                                const currentSize = formik.values.size
                                const isAllowedSize =
                                  currentSize === ESizesThaiPost.S100X150 ||
                                  currentSize === ESizesThaiPost.S100X180
                                if (!isAllowedSize) {
                                  formik.setFieldValue(
                                    'size',
                                    ESizesThaiPost.S100X180,
                                  )
                                }
                              }
                            }}
                          />
                        }
                        label="แสดงรายสินค้าในใบลาเบลแบบตาราง (100x150 หรือ 100x180)"
                      />
                    </Grid>
                  </>
                )}
                {isFlashSelfLabel && (
                  <>
                    <Grid item xs={12} lg={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showFullPhone}
                            onChange={(e) => setShowFullPhone(e.target.checked)}
                          />
                        }
                        label="แสดงเบอร์โทรผู้รับแบบเต็ม"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showItemInPack}
                            onChange={(e) => setShowItemInPack(e.target.checked)}
                          />
                        }
                        label="แสดงสินค้าย่อยของเซต"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showItemAsTable}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setShowItemAsTable(checked)
                              if (checked) {
                                formik.setFieldValue(
                                  'size',
                                  ESizesFlash.S100X180,
                                )
                              }
                            }}
                          />
                        }
                        label="แสดงรายการสินค้าแบบตาราง (100x180)"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid item xs={12} lg={12}>
              <Table
                size={'middle'}
                columns={columns}
                dataSource={formik.values.orders?.filter((order: ITracking) => order.status === EOrderStatus.TRACKING).map(
                  (tracking: any, index: number) => {
                    return {
                      ...tracking,
                      key: index,
                    }
                  },
                )}
                bordered
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <Grid
                container
                spacing={2}
                justifyContent="space-between"
                alignItems={'center'}
              >
                {isPrintSuccess && (
                  <>
                    <Grid item xs={7}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} lg={3}>
                          <TextField
                            onChange={(e) =>
                              setPrintReceipt({
                                ...printReceipt,
                                vat: e.target.value as VAT_TYPES,
                              })
                            }
                            fullWidth
                            label="ภาษี"
                            value={printReceipt.vat}
                            select
                            size="small"
                          >
                            <MenuItem value={'exclude'}>
                              แสดง vat 7% (นอก)
                            </MenuItem>
                            <MenuItem value={'include'}>
                              แสดง vat 7% (ใน)
                            </MenuItem>
                            <MenuItem value={'non-vat'}>
                              ไม่แสดง vat 7%
                            </MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} lg={3}>
                          <TextField
                            onChange={(e) =>
                              setPrintReceipt({
                                ...printReceipt,
                                showDate: e.target.value,
                              })
                            }
                            fullWidth
                            label="แสดงวันที่"
                            value={printReceipt.showDate}
                            select
                            size="small"
                          >
                            <MenuItem value={'order'}>
                              แสดง วันที่ของคำสั่งซื้อ
                            </MenuItem>
                            <MenuItem value={'date'}>
                              แสดง วันที่ปัจุบัน
                            </MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                          <Button
                            color="info"
                            type="button"
                            size="large"
                            variant="contained"
                            fullWidth
                            startIcon={<RiPrinterLine />}
                            onClick={() => {
                              setIsPrintReceipt(true)
                              formik.handleSubmit()
                            }}
                          >
                            พิมพ์ใบ LABEL และ ใบเสร็จ
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={5}>
                      <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item>
                          <Button
                            color="primary"
                            type="button"
                            size="large"
                            variant="contained"
                            startIcon={<RiPrinterLine />}
                            onClick={() => {
                              setIsPrintReceipt(false)
                              formik.handleSubmit()
                            }}
                          >
                            พิมพ์เฉพาะใบ LABEL
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            color="inherit"
                            size="large"
                            variant="contained"
                            onClick={() => onClose(false)}
                            startIcon={<ClearIcon />}
                          >
                            ปิดหน้าต่าง
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {/*<Grid item>*/}
                {/*  <Button*/}
                {/*    color="secondary"*/}
                {/*    type="submit"*/}
                {/*    size="large"*/}
                {/*    variant="contained"*/}
                {/*    startIcon={<SendIcon />}*/}
                {/*  >*/}
                {/*    ยืนยันยกเว้นพื้นที่พิเศษ*/}
                {/*  </Button>*/}
                {/*</Grid>*/}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
